import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import {
  Plus, Search, Briefcase, Clock, Euro, Users, Eye, Edit, Trash2,
  CheckCircle2, XCircle, Loader2, Send, FileText, Calendar, Star,
  ChevronRight, AlertCircle, User, Filter, Grid3X3, List, MessageSquare
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../../components/ui/dialog';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SPECIALTIES = [
  'Graphisme', 'Vidéo', 'Web', 'Mobile', 'Motion Design', '3D', 'Photo', 'Illustration', 'UX/UI', 'Rédaction', 'Marketing', 'SEO'
];

const PRIORITIES = [
  { value: 'low', label: 'Basse', color: 'bg-slate-100 text-slate-700' },
  { value: 'normal', label: 'Normale', color: 'bg-blue-100 text-blue-700' },
  { value: 'high', label: 'Haute', color: 'bg-orange-100 text-orange-700' },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-700' }
];

const STATUS_CONFIG = {
  draft: { label: 'Brouillon', color: 'bg-slate-100 text-slate-700', icon: FileText },
  open: { label: 'Ouvert', color: 'bg-green-100 text-green-700', icon: Users },
  in_progress: { label: 'En cours', color: 'bg-blue-100 text-blue-700', icon: Clock },
  completed: { label: 'Terminé', color: 'bg-purple-100 text-purple-700', icon: CheckCircle2 },
  cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-700', icon: XCircle }
};

const ProjectOffersPage = () => {
  const { user, token } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('offers');
  
  // Data states
  const [offers, setOffers] = useState([]);
  const [stats, setStats] = useState(null);
  
  // UI states
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSpecialty, setFilterSpecialty] = useState('all');
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form states
  const [offerForm, setOfferForm] = useState({
    title: '',
    description: '',
    detailed_brief: '',
    required_skills: [],
    budget_min: '',
    budget_max: '',
    budget_type: 'fixed',
    estimated_duration: '',
    deadline: '',
    priority: 'normal',
    status: 'draft'
  });
  
  const [applicationForm, setApplicationForm] = useState({
    cover_letter: '',
    pricing_type: 'fixed', // 'fixed' or 'hourly'
    proposed_budget: '',
    hourly_rate: '',
    estimated_hours: '',
    proposed_duration: '',
    availability: '',
    delivery_slot: '', // Créneau de livraison garanti
    can_start_date: ''
  });

  // Calculate budget automatically for hourly rate
  const calculatedBudget = applicationForm.pricing_type === 'hourly' 
    ? (parseFloat(applicationForm.hourly_rate || 0) * parseFloat(applicationForm.estimated_hours || 0))
    : parseFloat(applicationForm.proposed_budget || 0);

  const isAgency = user?.role === 'agency';
  const isCreative = user?.role === 'team_member';

  // Fetch offers
  const fetchOffers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterSpecialty !== 'all') params.append('specialty', filterSpecialty);
      
      const queryString = params.toString();
      const response = await api.get(`/project-offers/${queryString ? '?' + queryString : ''}`);
      setOffers(response.data.offers || []);
      
      // Fetch stats if agency
      if (isAgency) {
        const statsRes = await api.get('/project-offers/stats/overview');
        setStats(statsRes.data);
      }
    } catch (error) {
      console.error('Error fetching offers:', error);
      toast.error('Erreur lors du chargement des offres');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterSpecialty, isAgency]);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  // Create/Update offer
  const handleSaveOffer = async () => {
    if (!offerForm.title) {
      toast.error('Le titre est requis');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...offerForm,
        budget_min: parseFloat(offerForm.budget_min) || 0,
        budget_max: parseFloat(offerForm.budget_max) || 0
      };
      
      if (selectedOffer) {
        await api.put(`/project-offers/${selectedOffer.id}`, payload);
        toast.success('Offre mise à jour');
      } else {
        await api.post('/project-offers/', payload);
        toast.success('Offre créée');
      }
      
      setShowCreateModal(false);
      resetForm();
      fetchOffers();
    } catch (error) {
      console.error('Error saving offer:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  // Delete offer
  const handleDeleteOffer = async (offerId) => {
    if (!window.confirm('Supprimer cette offre ?')) return;
    
    try {
      await api.delete(`/project-offers/${offerId}`);
      toast.success('Offre supprimée');
      fetchOffers();
    } catch (error) {
      console.error('Error deleting offer:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Apply to offer (for creatives)
  const handleApply = async () => {
    if (!applicationForm.cover_letter) {
      toast.error('Une lettre de motivation est requise');
      return;
    }
    
    // Validate pricing
    if (applicationForm.pricing_type === 'fixed' && !applicationForm.proposed_budget) {
      toast.error('Veuillez indiquer votre tarif forfaitaire');
      return;
    }
    if (applicationForm.pricing_type === 'hourly' && (!applicationForm.hourly_rate || !applicationForm.estimated_hours)) {
      toast.error('Veuillez indiquer votre tarif horaire et le nombre d\'heures estimées');
      return;
    }
    
    setSaving(true);
    try {
      const finalBudget = applicationForm.pricing_type === 'hourly' 
        ? calculatedBudget 
        : parseFloat(applicationForm.proposed_budget);
        
      await api.post(`/project-offers/${selectedOffer.id}/apply`, {
        cover_letter: applicationForm.cover_letter,
        pricing_type: applicationForm.pricing_type,
        proposed_budget: finalBudget,
        hourly_rate: applicationForm.pricing_type === 'hourly' ? parseFloat(applicationForm.hourly_rate) : null,
        estimated_hours: applicationForm.pricing_type === 'hourly' ? parseFloat(applicationForm.estimated_hours) : null,
        proposed_duration: applicationForm.proposed_duration,
        availability: applicationForm.availability,
        delivery_slot: applicationForm.delivery_slot,
        can_start_date: applicationForm.can_start_date
      });
      toast.success('Candidature envoyée !');
      setShowApplicationModal(false);
      setApplicationForm({ 
        cover_letter: '', 
        pricing_type: 'fixed',
        proposed_budget: '', 
        hourly_rate: '',
        estimated_hours: '',
        proposed_duration: '', 
        availability: '',
        delivery_slot: '',
        can_start_date: ''
      });
      fetchOffers();
    } catch (error) {
      console.error('Error applying:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSaving(false);
    }
  };

  // Update application status
  const handleUpdateApplication = async (offerId, appId, status) => {
    try {
      await api.put(`/project-offers/${offerId}/applications/${appId}`, { status });
      toast.success(status === 'accepted' ? 'Candidature acceptée' : 'Candidature rejetée');
      
      // Refresh offer details
      const response = await api.get(`/project-offers/${offerId}`);
      setSelectedOffer(response.data);
      fetchOffers();
    } catch (error) {
      console.error('Error updating application:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Open offer detail
  const openOfferDetail = async (offer) => {
    try {
      const response = await api.get(`/project-offers/${offer.id}`);
      setSelectedOffer(response.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error fetching offer details:', error);
      toast.error('Erreur lors du chargement');
    }
  };

  // Edit offer
  const editOffer = (offer) => {
    setSelectedOffer(offer);
    setOfferForm({
      title: offer.title || '',
      description: offer.description || '',
      detailed_brief: offer.detailed_brief || '',
      required_skills: offer.required_skills || [],
      budget_min: offer.budget_min?.toString() || '',
      budget_max: offer.budget_max?.toString() || '',
      budget_type: offer.budget_type || 'fixed',
      estimated_duration: offer.estimated_duration || '',
      deadline: offer.deadline || '',
      priority: offer.priority || 'normal',
      status: offer.status || 'draft'
    });
    setShowCreateModal(true);
  };

  const resetForm = () => {
    setSelectedOffer(null);
    setOfferForm({
      title: '',
      description: '',
      detailed_brief: '',
      required_skills: [],
      budget_min: '',
      budget_max: '',
      budget_type: 'fixed',
      estimated_duration: '',
      deadline: '',
      priority: 'normal',
      status: 'draft'
    });
  };

  // Filter offers
  const filteredOffers = offers.filter(offer => {
    const matchesSearch = offer.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         offer.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Get status badge
  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
    const IconComponent = config.icon;
    return (
      <Badge className={cn("flex items-center gap-1", config.color)}>
        <IconComponent className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  // Get priority badge
  const getPriorityBadge = (priority) => {
    const config = PRIORITIES.find(p => p.value === priority) || PRIORITIES[1];
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex">
      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />

      <div className={`flex-1 transition-all duration-300 ${collapsed ? 'lg:ml-20' : 'lg:ml-72'}`}>
        <Header setMobileOpen={setMobileOpen} />

        <main className="p-4 md:p-6 pt-24">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isAgency ? 'Offres de Projets' : 'Missions Disponibles'}
              </h1>
              <p className="text-gray-500">
                {isAgency ? 'Publiez des missions pour vos créatifs' : 'Découvrez les opportunités de projets'}
              </p>
            </div>
            {isAgency && (
              <Button onClick={() => { resetForm(); setShowCreateModal(true); }} className="gap-2">
                <Plus className="w-4 h-4" />
                Nouvelle Offre
              </Button>
            )}
          </div>

          {/* Stats (Agency only) */}
          {isAgency && stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Briefcase className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.offers?.total || 0}</p>
                      <p className="text-xs text-gray-500">Total Offres</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Users className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.offers?.open || 0}</p>
                      <p className="text-xs text-gray-500">Ouvertes</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <Clock className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.offers?.in_progress || 0}</p>
                      <p className="text-xs text-gray-500">En cours</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <MessageSquare className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.applications?.pending || 0}</p>
                      <p className="text-xs text-gray-500">Candidatures en attente</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-48"
                />
              </div>
              {isAgency && (
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="draft">Brouillon</SelectItem>
                    <SelectItem value="open">Ouvert</SelectItem>
                    <SelectItem value="in_progress">En cours</SelectItem>
                    <SelectItem value="completed">Terminé</SelectItem>
                  </SelectContent>
                </Select>
              )}
              <Select value={filterSpecialty} onValueChange={setFilterSpecialty}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="Spécialité" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  {SPECIALTIES.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex border rounded-lg">
              <Button
                variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                size="icon"
                onClick={() => setViewMode('grid')}
                className="rounded-r-none"
              >
                <Grid3X3 className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                size="icon"
                onClick={() => setViewMode('list')}
                className="rounded-l-none"
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Offers List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : filteredOffers.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Briefcase className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 mb-4">
                  {isAgency ? 'Aucune offre créée' : 'Aucune mission disponible'}
                </p>
                {isAgency && (
                  <Button onClick={() => setShowCreateModal(true)} variant="outline" className="gap-2">
                    <Plus className="w-4 h-4" />
                    Créer une offre
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredOffers.map(offer => (
                <Card key={offer.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => openOfferDetail(offer)}>
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <h3 className="font-semibold text-gray-900 line-clamp-2">{offer.title}</h3>
                      {getStatusBadge(offer.status)}
                    </div>
                    
                    <p className="text-sm text-gray-600 line-clamp-2 mb-4">{offer.description}</p>
                    
                    {/* Skills */}
                    <div className="flex flex-wrap gap-1 mb-4">
                      {(offer.required_skills || []).slice(0, 3).map(skill => (
                        <Badge key={skill} variant="outline" className="text-xs">{skill}</Badge>
                      ))}
                      {(offer.required_skills || []).length > 3 && (
                        <Badge variant="outline" className="text-xs">+{offer.required_skills.length - 3}</Badge>
                      )}
                    </div>
                    
                    {/* Budget & Duration */}
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <div className="flex items-center gap-1">
                        <Euro className="w-4 h-4" />
                        <span>
                          {offer.budget_min && offer.budget_max 
                            ? `${offer.budget_min}€ - ${offer.budget_max}€`
                            : 'Budget à définir'}
                        </span>
                      </div>
                      {offer.estimated_duration && (
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>{offer.estimated_duration}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Footer */}
                    <div className="flex items-center justify-between pt-3 border-t">
                      {getPriorityBadge(offer.priority)}
                      {isAgency && offer.application_count !== undefined && (
                        <span className="text-sm text-gray-500">
                          {offer.application_count} candidature{offer.application_count > 1 ? 's' : ''}
                        </span>
                      )}
                      {isCreative && offer.has_applied && (
                        <Badge variant="outline" className="text-green-600 border-green-300">
                          Candidature envoyée
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <div className="divide-y">
                {filteredOffers.map(offer => (
                  <div key={offer.id} className="p-4 flex items-center gap-4 hover:bg-gray-50 cursor-pointer" onClick={() => openOfferDetail(offer)}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-gray-900">{offer.title}</h3>
                        {getStatusBadge(offer.status)}
                      </div>
                      <p className="text-sm text-gray-500 line-clamp-1">{offer.description}</p>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Euro className="w-4 h-4" />
                        {offer.budget_max ? `${offer.budget_max}€` : '-'}
                      </div>
                      {isAgency && (
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {offer.application_count || 0}
                        </div>
                      )}
                      <ChevronRight className="w-4 h-4" />
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </main>
      </div>

      {/* Create/Edit Offer Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedOffer ? 'Modifier l\'offre' : 'Nouvelle Offre de Projet'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Titre de la mission *</Label>
              <Input
                value={offerForm.title}
                onChange={(e) => setOfferForm({ ...offerForm, title: e.target.value })}
                placeholder="Ex: Création de logo pour startup tech"
              />
            </div>
            
            <div>
              <Label>Description courte</Label>
              <Textarea
                value={offerForm.description}
                onChange={(e) => setOfferForm({ ...offerForm, description: e.target.value })}
                placeholder="Résumé de la mission..."
                rows={2}
              />
            </div>
            
            <div>
              <Label>Brief détaillé</Label>
              <Textarea
                value={offerForm.detailed_brief}
                onChange={(e) => setOfferForm({ ...offerForm, detailed_brief: e.target.value })}
                placeholder="Décrivez en détail les attentes, le contexte, les livrables..."
                rows={4}
              />
            </div>
            
            <div>
              <Label>Compétences requises</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {SPECIALTIES.map(skill => (
                  <Badge
                    key={skill}
                    variant={offerForm.required_skills.includes(skill) ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => {
                      const skills = offerForm.required_skills.includes(skill)
                        ? offerForm.required_skills.filter(s => s !== skill)
                        : [...offerForm.required_skills, skill];
                      setOfferForm({ ...offerForm, required_skills: skills });
                    }}
                  >
                    {skill}
                  </Badge>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Budget minimum (€)</Label>
                <Input
                  type="number"
                  value={offerForm.budget_min}
                  onChange={(e) => setOfferForm({ ...offerForm, budget_min: e.target.value })}
                  placeholder="500"
                />
              </div>
              <div>
                <Label>Budget maximum (€)</Label>
                <Input
                  type="number"
                  value={offerForm.budget_max}
                  onChange={(e) => setOfferForm({ ...offerForm, budget_max: e.target.value })}
                  placeholder="2000"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Durée estimée</Label>
                <Input
                  value={offerForm.estimated_duration}
                  onChange={(e) => setOfferForm({ ...offerForm, estimated_duration: e.target.value })}
                  placeholder="2 semaines"
                />
              </div>
              <div>
                <Label>Date limite</Label>
                <Input
                  type="date"
                  value={offerForm.deadline}
                  onChange={(e) => setOfferForm({ ...offerForm, deadline: e.target.value })}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Priorité</Label>
                <Select value={offerForm.priority} onValueChange={(val) => setOfferForm({ ...offerForm, priority: val })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Statut</Label>
                <Select value={offerForm.status} onValueChange={(val) => setOfferForm({ ...offerForm, status: val })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Brouillon</SelectItem>
                    <SelectItem value="open">Publier (ouvert)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveOffer} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {selectedOffer ? 'Mettre à jour' : 'Créer l\'offre'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Offer Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedOffer && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <DialogTitle className="text-xl">{selectedOffer.title}</DialogTitle>
                    <div className="flex items-center gap-2 mt-2">
                      {getStatusBadge(selectedOffer.status)}
                      {getPriorityBadge(selectedOffer.priority)}
                    </div>
                  </div>
                  {isAgency && (
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => { setShowDetailModal(false); editOffer(selectedOffer); }}>
                        <Edit className="w-4 h-4 mr-1" />
                        Modifier
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => { handleDeleteOffer(selectedOffer.id); setShowDetailModal(false); }}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                {/* Description */}
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Description</h4>
                  <p className="text-gray-600">{selectedOffer.description || 'Aucune description'}</p>
                </div>
                
                {/* Brief */}
                {selectedOffer.detailed_brief && (
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">Brief détaillé</h4>
                    <p className="text-gray-600 whitespace-pre-wrap">{selectedOffer.detailed_brief}</p>
                  </div>
                )}
                
                {/* Skills */}
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Compétences requises</h4>
                  <div className="flex flex-wrap gap-2">
                    {(selectedOffer.required_skills || []).map(skill => (
                      <Badge key={skill} variant="outline">{skill}</Badge>
                    ))}
                    {(selectedOffer.required_skills || []).length === 0 && (
                      <span className="text-gray-400 text-sm">Aucune spécifiée</span>
                    )}
                  </div>
                </div>
                
                {/* Budget & Duration */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <h4 className="font-medium text-gray-700 text-sm mb-1">Budget</h4>
                    <p className="text-gray-900">
                      {selectedOffer.budget_min && selectedOffer.budget_max 
                        ? `${selectedOffer.budget_min}€ - ${selectedOffer.budget_max}€`
                        : 'À définir'}
                    </p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-700 text-sm mb-1">Durée</h4>
                    <p className="text-gray-900">{selectedOffer.estimated_duration || 'Non définie'}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-700 text-sm mb-1">Deadline</h4>
                    <p className="text-gray-900">{selectedOffer.deadline || 'Non définie'}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-700 text-sm mb-1">Publié le</h4>
                    <p className="text-gray-900">
                      {selectedOffer.published_at 
                        ? new Date(selectedOffer.published_at).toLocaleDateString('fr-FR')
                        : 'Non publié'}
                    </p>
                  </div>
                </div>
                
                {/* Applications (Agency only) */}
                {isAgency && selectedOffer.applications && (
                  <div>
                    <h4 className="font-medium text-gray-700 mb-3">
                      Candidatures ({selectedOffer.applications.length})
                    </h4>
                    {selectedOffer.applications.length === 0 ? (
                      <p className="text-gray-400 text-sm">Aucune candidature pour le moment</p>
                    ) : (
                      <div className="space-y-3">
                        {selectedOffer.applications.map(app => (
                          <Card key={app.id}>
                            <CardContent className="py-3">
                              <div className="flex items-start justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                                    {app.creative?.avatar ? (
                                      <img src={app.creative.avatar} alt="" className="w-full h-full rounded-full object-cover" />
                                    ) : (
                                      <User className="w-5 h-5 text-gray-400" />
                                    )}
                                  </div>
                                  <div>
                                    <p className="font-medium">{app.creative?.name || 'Créatif'}</p>
                                    <div className="flex items-center gap-2 text-sm text-gray-500">
                                      <span>{app.creative?.email}</span>
                                      {app.creative?.average_rating > 0 && (
                                        <span className="flex items-center gap-1">
                                          <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                                          {app.creative.average_rating}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {app.status === 'pending' ? (
                                    <>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={(e) => { e.stopPropagation(); handleUpdateApplication(selectedOffer.id, app.id, 'rejected'); }}
                                      >
                                        <XCircle className="w-4 h-4 text-red-500" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        onClick={(e) => { e.stopPropagation(); handleUpdateApplication(selectedOffer.id, app.id, 'accepted'); }}
                                      >
                                        <CheckCircle2 className="w-4 h-4 mr-1" />
                                        Accepter
                                      </Button>
                                    </>
                                  ) : (
                                    <Badge className={app.status === 'accepted' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                                      {app.status === 'accepted' ? 'Accepté' : 'Refusé'}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              {app.cover_letter && (
                                <p className="mt-3 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                                  "{app.cover_letter}"
                                </p>
                              )}
                              <div className="flex gap-4 mt-2 text-sm text-gray-500">
                                {app.proposed_budget > 0 && (
                                  <span>Budget proposé: {app.proposed_budget}€</span>
                                )}
                                {app.proposed_duration && (
                                  <span>Durée: {app.proposed_duration}</span>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Apply Button (Creative only) */}
                {isCreative && selectedOffer.status === 'open' && !selectedOffer.my_application && (
                  <Button 
                    className="w-full" 
                    onClick={() => { setShowDetailModal(false); setShowApplicationModal(true); }}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Postuler à cette mission
                  </Button>
                )}
                
                {isCreative && selectedOffer.my_application && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="font-medium">Vous avez postulé à cette mission</span>
                    </div>
                    <p className="text-sm text-green-600 mt-1">
                      Statut: {selectedOffer.my_application.status === 'pending' ? 'En attente de réponse' : 
                               selectedOffer.my_application.status === 'accepted' ? 'Acceptée !' : 'Refusée'}
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Application Modal (Creative) */}
      <Dialog open={showApplicationModal} onOpenChange={setShowApplicationModal}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Postuler à cette mission</DialogTitle>
            <DialogDescription>
              {selectedOffer?.title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-5 py-4">
            {/* Motivation */}
            <div>
              <Label>Lettre de motivation *</Label>
              <Textarea
                value={applicationForm.cover_letter}
                onChange={(e) => setApplicationForm({ ...applicationForm, cover_letter: e.target.value })}
                placeholder="Expliquez pourquoi vous êtes le bon créatif pour cette mission..."
                rows={3}
              />
            </div>
            
            {/* Pricing Type Selection */}
            <div className="p-4 bg-slate-50 rounded-lg space-y-4">
              <Label className="text-base font-semibold">Mode de tarification</Label>
              
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setApplicationForm({ ...applicationForm, pricing_type: 'fixed' })}
                  className={cn(
                    "p-3 rounded-lg border-2 text-left transition-all",
                    applicationForm.pricing_type === 'fixed' 
                      ? "border-blue-500 bg-blue-50" 
                      : "border-slate-200 hover:border-slate-300"
                  )}
                >
                  <div className="font-medium">💰 Prix fixe</div>
                  <div className="text-xs text-slate-500">Tarif forfaitaire global</div>
                </button>
                
                <button
                  type="button"
                  onClick={() => setApplicationForm({ ...applicationForm, pricing_type: 'hourly' })}
                  className={cn(
                    "p-3 rounded-lg border-2 text-left transition-all",
                    applicationForm.pricing_type === 'hourly' 
                      ? "border-blue-500 bg-blue-50" 
                      : "border-slate-200 hover:border-slate-300"
                  )}
                >
                  <div className="font-medium">⏱️ Tarif horaire</div>
                  <div className="text-xs text-slate-500">Calcul automatique</div>
                </button>
              </div>
              
              {/* Fixed Price Input */}
              {applicationForm.pricing_type === 'fixed' && (
                <div>
                  <Label>Tarif forfaitaire (€ HT) *</Label>
                  <Input
                    type="number"
                    value={applicationForm.proposed_budget}
                    onChange={(e) => setApplicationForm({ ...applicationForm, proposed_budget: e.target.value })}
                    placeholder="1500"
                    className="text-lg font-semibold"
                  />
                </div>
              )}
              
              {/* Hourly Rate Calculator */}
              {applicationForm.pricing_type === 'hourly' && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Tarif horaire (€ HT) *</Label>
                      <Input
                        type="number"
                        value={applicationForm.hourly_rate}
                        onChange={(e) => setApplicationForm({ ...applicationForm, hourly_rate: e.target.value })}
                        placeholder="75"
                      />
                    </div>
                    <div>
                      <Label>Heures estimées *</Label>
                      <Input
                        type="number"
                        value={applicationForm.estimated_hours}
                        onChange={(e) => setApplicationForm({ ...applicationForm, estimated_hours: e.target.value })}
                        placeholder="20"
                      />
                    </div>
                  </div>
                  
                  {/* Auto-calculated total */}
                  {calculatedBudget > 0 && (
                    <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-emerald-700">Budget total calculé</span>
                        <span className="text-xl font-bold text-emerald-700">
                          {calculatedBudget.toLocaleString('fr-FR')}€ HT
                        </span>
                      </div>
                      <div className="text-xs text-emerald-600 mt-1">
                        {applicationForm.hourly_rate}€/h × {applicationForm.estimated_hours}h
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Delivery Slot - Créneau garanti */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>📅 Créneau de livraison garanti</Label>
                <Select 
                  value={applicationForm.delivery_slot} 
                  onValueChange={(val) => setApplicationForm({ ...applicationForm, delivery_slot: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1_week">Sous 1 semaine</SelectItem>
                    <SelectItem value="2_weeks">Sous 2 semaines</SelectItem>
                    <SelectItem value="3_weeks">Sous 3 semaines</SelectItem>
                    <SelectItem value="1_month">Sous 1 mois</SelectItem>
                    <SelectItem value="custom">Date personnalisée</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>🚀 Disponible à partir du</Label>
                <Input
                  type="date"
                  value={applicationForm.can_start_date}
                  onChange={(e) => setApplicationForm({ ...applicationForm, can_start_date: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Informations complémentaires</Label>
              <Input
                value={applicationForm.availability}
                onChange={(e) => setApplicationForm({ ...applicationForm, availability: e.target.value })}
                placeholder="Ex: Disponible les matins, travaille en remote..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApplicationModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleApply} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Envoyer ma candidature
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectOffersPage;
